import {useEffect, useRef, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router'
import AuthLayout from './AuthLayout'
import {logoLatest} from '@/assets/images'
import {useAuth} from '@/context/AuthContext'
import {getToken} from '@/utils/authToken'
import {consumeRedirectAfterLogin} from '@/utils/functions'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import {
    getPhoneBookingSummary,
    requestPhoneBookingOtp,
    verifyPhoneBookingOtp,
} from '@/services/phoneBookingAuthService'

/**
 * `/authorize?ref=…` — proving a handoff link belongs to you.
 *
 * RefAuthGate sends people here when a `?ref=` link lands without a session, or with a
 * session belonging to someone else. The `ref` is all this page has: it never asks for a
 * phone number, because the backend already knows which one the booking is on and only
 * hands back the last four digits to name it on screen.
 *
 * The return trip is the same contract SignIn uses — the gate stashed the URL with
 * `saveRedirectAfterLogin`, and a verified code sends the passenger straight there. When
 * nothing stashed one (this URL was opened directly), `destination()` rebuilds it from the
 * `trip_id` the summary returned: a ref names one booking, so there is always somewhere
 * better to go than the dashboard home page.
 *
 * The OTP box is deliberately the same six-input pattern as OtpVerification (paste, back
 * -space walk-back, resend timer) so the two screens don't feel like different products.
 */

const OTP_LENGTH = 6
// Fallback for the resend countdown. BOOKING_CONSTRAINTS reads /config/app, and this is
// the one screen a passenger reaches COLD — straight off an SMS, before that request has
// landed — so the getter can still be undefined here. Unguarded it made the countdown
// tick NaN. Read inside the component, never at module level.
const RESEND_SECONDS_FALLBACK = 60

export default function Authorize() {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const {login} = useAuth()

    const ref = searchParams.get('ref') || ''

    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''))
    const otpRefs = useRef([])
    const [phoneLast4, setPhoneLast4] = useState('')
    // The trip this ref points at, from the summary — the fallback destination.
    const [tripId, setTripId] = useState(null)
    // A Pay Later booking still waiting for its card: the fallback destination is its payment link, not the draft confirm.
    const [awaitingPayment, setAwaitingPayment] = useState(false)
    // Blocks the form until the first request-otp settles — typing a code before one has
    // been sent can only fail.
    const [sending, setSending] = useState(true)
    const [verifying, setVerifying] = useState(false)
    const [error, setError] = useState('')
    // The first code never went out — don't tell the passenger "we texted a code".
    const [sendFailed, setSendFailed] = useState(false)
    // Set when there is nothing to retry: a dead ref, or an account with no phone. The
    // form comes off screen, because resending would fail the same way every time.
    const [fatal, setFatal] = useState('')
    const [resendMessage, setResendMessage] = useState('')
    const resendSeconds = BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS || RESEND_SECONDS_FALLBACK
    const [resendTimer, setResendTimer] = useState(resendSeconds)
    // Guards StrictMode's double-invoked effect — one code per arrival, not two.
    const startedRef = useRef(null)

    // No ref, no page. Someone typed the URL, or a link lost its query string.
    useEffect(() => {
        if (!ref) navigate('/signin', {replace: true})
    }, [ref, navigate])

    // Summary first (for the "•••1234" line), then send the code. The summary is a nicety
    // — if it fails we still send, and the copy just says "your phone".
    useEffect(() => {
        if (!ref || startedRef.current === ref) return
        startedRef.current = ref

        ;(async () => {
            const summary = await getPhoneBookingSummary(ref)
            // A ref names exactly one trip, and the summary hands us its id — so this page
            // can always work out where it is sending the passenger, even when nothing
            // saved a return path (the URL was opened directly rather than followed from
            // the booking page). See `destination` below.
            if (summary.data?.trip_id != null) setTripId(summary.data.trip_id)
            if (summary.data?.awaiting_payment) setAwaitingPayment(true)
            if (summary.success && summary.data?.phone_last4) {
                setPhoneLast4(summary.data.phone_last4)
            } else if (summary.statusCode === 404) {
                setSending(false)
                setFatal(summary.message || 'This link is no longer valid. Please ask us to send a new one.')
                return
            }

            const sent = await requestPhoneBookingOtp(ref)
            setSending(false)

            if (!sent.success) {
                // No phone on the account is the one failure a passenger can't fix here.
                if (sent.statusCode === 422) {
                    setFatal(sent.message || "We don't have a phone number on this booking. Please call us to confirm it.")
                    return
                }
                setSendFailed(true)
                setError(sent.message || 'We could not send the code. Try again.')
                return
            }
            if (sent.data?.phone_last4) setPhoneLast4(sent.data.phone_last4)
            setTimeout(() => otpRefs.current[0]?.focus(), 100)
        })()
    }, [ref])

    // Resend countdown.
    useEffect(() => {
        if (resendTimer <= 0) return
        const id = setInterval(() => setResendTimer((t) => (t <= 1 ? 0 : t - 1)), 1000)
        return () => clearInterval(id)
    }, [resendTimer])

    // Mirror of `otp` that is current DURING an event, before React re-renders — the
    // focus guard runs synchronously off a change, when `otp` is still pre-keystroke.
    const otpNowRef = useRef(otp)
    useEffect(() => {
        otpNowRef.current = otp
    }, [otp])

    // Replaces the old `readOnly` on later boxes, which is what broke iOS: focus moved
    // onto a read-only input and Safari closed the keyboard. Guarding on focus keeps the
    // same "can't skip ahead" behaviour with every box writable.
    const handleOtpFocus = (idx) => {
        const now = otpNowRef.current
        const firstEmpty = now.findIndex((d) => d === '')
        const target = firstEmpty === -1 ? now.length - 1 : firstEmpty
        if (idx > target) otpRefs.current[target]?.focus()
    }

    const handleOtpChange = (value, idx) => {
        const digit = value.replace(/\D/g, '').slice(-1)
        const next = [...otp]
        next[idx] = digit
        otpNowRef.current = next
        setOtp(next)
        setError('')
        if (digit && idx < OTP_LENGTH - 1) otpRefs.current[idx + 1]?.focus()
    }

    const handleOtpKeyDown = (e, idx) => {
        if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
            otpRefs.current[idx - 1]?.focus()
        }
    }

    // A pasted code fills the whole row — passengers copy the SMS rather than retype it.
    const handleOtpPaste = (e) => {
        e.preventDefault()
        const digits = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH)
        if (!digits) return
        const next = Array(OTP_LENGTH).fill('')
        digits.split('').forEach((d, i) => { next[i] = d })
        setOtp(next)
        setError('')
        otpRefs.current[Math.min(digits.length, OTP_LENGTH - 1)]?.focus()
    }

    /**
     * Where a verified passenger goes.
     *
     * 1. The path the gate stashed on the way in — the page the link actually pointed at.
     * 2. Otherwise the booking this ref names. `/authorize?ref=…` opened DIRECTLY (pasted,
     *    or handed out as-is) has no stashed path, and used to fall back to `/` — a
     *    dashboard home page that has nothing to do with why they came. The summary gave
     *    us `trip_id`, so we can send them to the actual booking instead.
     * 3. Only then home.
     *
     * Guarded against this page: a destination of `/authorize` navigates the passenger to
     * the screen they are standing on, which reads as "the code did nothing". The saver
     * refuses to store one now, but a stale value can still be sitting in sessionStorage
     * from before that fix.
     */
    const destination = () => {
        const saved = consumeRedirectAfterLogin()
        if (saved && !saved.startsWith('/authorize')) return saved
        // No `ref` on the way out — it got the passenger through this page and is not
        // needed again, so it does not belong in the address bar of the booking.
        if (tripId != null) return `/rides/${tripId}/${awaitingPayment ? 'complete-payment' : 'phone-booking-confirmation'}?layout=minimal`
        return '/'
    }

    const handleVerify = async () => {
        const code = otp.join('')
        if (code.length !== OTP_LENGTH) {
            setError(`Enter the ${OTP_LENGTH}-digit code.`)
            return
        }

        setVerifying(true)
        setError('')
        const result = await verifyPhoneBookingOtp(ref, code)
        setVerifying(false)

        if (!result.success || !result.token) {
            setError(result.message || 'That code did not work. Try again.')
            setOtp(Array(OTP_LENGTH).fill(''))
            setTimeout(() => otpRefs.current[0]?.focus(), 100)
            return
        }

        // `login` writes the token to the SAME cookie every other screen reads
        // (`hopn_login_token`, utils/authToken) and flips AuthContext. What comes back
        // from verify-otp is an ordinary passenger Sanctum token — the same thing
        // /signin produces — so from here the session is indistinguishable from one made
        // by signing in normally.
        login(result.token)

        // The cookie IS the session: apiClient reads it for every Authorization header,
        // and AuthContext re-reads it on focus. If it didn't stick (cookies blocked, or
        // a private-window quirk), navigating on lands the passenger on a page that 401s
        // and bounces them straight back here for another code — a loop that looks like
        // "the OTP does nothing". Say what happened instead.
        if (!getToken()) {
            setError('We could not keep you signed in on this device. Check that cookies are enabled, then try again.')
            return
        }

        // ⚠️ NO IDENTITY-GATE DIVERSION HERE, on purpose. A support agent sends this link so
        // the passenger can confirm a desk-created booking, whose identity is a placeholder
        // until they claim it. Routing them to /settings (postLoginPath) meant the link
        // never reached the confirm page. The destination is shown instead, and the
        // confirm page states what is missing if the backend refuses the confirm
        // (`identity_incomplete` -> IdentityIncompleteNotice).
        navigate(destination(), {replace: true})
    }

    const handleResend = async () => {
        if (resendTimer > 0 || sending) return
        setSending(true)
        setResendMessage('Sending...')
        const sent = await requestPhoneBookingOtp(ref)
        setSending(false)

        if (sent.success) {
            setSendFailed(false)
            setResendMessage('Code resent')
            setResendTimer(resendSeconds)
        } else {
            setResendMessage(sent.message || 'Could not resend the code')
        }
        setTimeout(() => setResendMessage(''), 3000)
    }

    const formatTimer = (seconds) => {
        const m = Math.floor(seconds / 60)
        const s = seconds % 60
        return `${m}:${s.toString().padStart(2, '0')}`
    }

    if (!ref) return null

    return (
        <AuthLayout>
            <div className="flex flex-col flex-1 w-full overflow-y-auto lg:w-1/2 no-scrollbar">
                <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                    {/* AuthLayout's branded panel is lg-only, so on a phone this screen carried no
                        logo at all. Same asset, shown only where that panel isn't. */}
                    <div className="flex justify-center mb-6 lg:hidden">
                        <img src={logoLatest} alt="hop'n" className="h-9 w-auto"/>
                    </div>

                    <div className="mb-5 sm:mb-8 text-center">
                        <h1 className="mb-2 font-semibold text-gray-800 text-2xl sm:text-3xl">
                            Confirm it's you
                        </h1>
                        {!fatal && !sendFailed && (
                            <p className="text-base font-bold text-gray-500">
                                {phoneLast4
                                    ? <>We texted a code to the number ending <span className="font-bold text-gray-700">•••{phoneLast4}</span>.</>
                                    : 'We texted a code to the phone number on your booking.'}
                            </p>
                        )}
                    </div>

                    {fatal ? (
                        <div className="text-center">
                            <p className="text-sm text-red-500 mb-6">{fatal}</p>
                            <button
                                type="button"
                                onClick={() => navigate('/signin', {replace: true})}
                                className="text-sm font-medium text-gray-800 hover:text-gray-600"
                            >
                                Go to sign in
                            </button>
                        </div>
                    ) : (
                        <div>
                            <h2 className="text-base font-medium text-gray-700 text-center mb-4">
                                Enter the code
                            </h2>

                            {/* Grid, not a fixed-width row: six 48px boxes plus gaps
                                overflowed a narrow phone and the first/last were clipped. */}
                            <div className="grid grid-cols-6 gap-2 sm:gap-3 justify-items-center mb-6">
                                {otp.map((digit, idx) => (
                                    <input
                                        key={idx}
                                        ref={(el) => (otpRefs.current[idx] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={1}
                                        value={digit}
                                        disabled={sending}
                                        onChange={(e) => handleOtpChange(e.target.value, idx)}
                                        onFocus={() => handleOtpFocus(idx)}
                                        onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                                        onPaste={handleOtpPaste}
                                        className="w-full max-w-12 min-w-0 h-12 sm:h-14 text-center text-lg font-semibold text-gray-800 border border-gray-300 rounded-xl bg-transparent focus:border-gray-500 focus:outline-none disabled:opacity-60"
                                    />
                                ))}
                            </div>

                            {error && <p className="text-sm text-red-500 text-center mb-4">{error}</p>}

                            <div className="flex items-center justify-center gap-3 mb-6">
                                <button
                                    type="button"
                                    disabled={resendTimer > 0 || sending}
                                    onClick={handleResend}
                                    className={`text-sm font-medium ${
                                        resendTimer === 0 && !sending
                                            ? 'text-gray-800 hover:text-gray-600 cursor-pointer'
                                            : 'text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    Resend code
                                </button>
                                {resendTimer > 0 && (
                                    <span className="text-sm text-gray-500">{formatTimer(resendTimer)}</span>
                                )}
                            </div>

                            {resendMessage && (
                                <p className="text-sm text-center text-gray-600 mb-4">{resendMessage}</p>
                            )}

                            <button
                                type="button"
                                onClick={handleVerify}
                                disabled={verifying || sending}
                                className="relative flex items-center justify-center w-full h-14 rounded-full bg-gray-900 text-white text-sm font-medium transition hover:bg-gray-800 disabled:opacity-60"
                            >
                                {verifying ? 'Verifying...' : sending ? 'Sending code...' : 'Continue'}
                                <span className="absolute right-2 flex items-center justify-center w-10 h-10 rounded-full bg-emerald-300">
                                    <svg className="size-5 text-gray-900" viewBox="0 0 20 20" fill="none">
                                        <path
                                            d="M4.167 10h11.666M10.833 5l5 5-5 5"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </AuthLayout>
    )
}
